import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { APPINSIGHTS_CONNECTION_STRING } from '@env';

let appInsights: ApplicationInsights | null = null;

export function initializeAppInsights(): void {
  // Prevent double initialization
  if (appInsights) {
    return;
  }

  const connectionString = APPINSIGHTS_CONNECTION_STRING;

  // Only initialize if connection string is available
  if (!connectionString) {
    console.warn('Application Insights connection string not found');
    return;
  }

  try {
    appInsights = new ApplicationInsights({
      config: {
        connectionString,
        enableAutoRouteTracking: true,
        enableCorsCorrelation: true,
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        disableFetchTracking: false,
        disableAjaxTracking: false,
        excludeRequestFromAutoTrackingPatterns: [ /* JM: I've added this to prevent tracking Mixpanel requests, so that MixPanel works correctly alongside App Insights without CORS issues. */
          /api-js\.mixpanel\.com/
        ],
      },
    });

    appInsights.loadAppInsights();
    appInsights.trackPageView();

  } catch (error) {
    console.error('Failed to initialize Application Insights:', error);
  }
}

export function getAppInsights(): ApplicationInsights | null {
  return appInsights;
}

export function setAuthenticatedUserContext(userId: string, accountId?: string): void {
  if (!appInsights) {
    return;
  }

  if (!userId) {
    return;
  }

  // Important: avoid using PII (like email) as userId.
  // Use a stable, non-PII identifier (e.g., Firebase UID).
  appInsights.setAuthenticatedUserContext(userId, accountId, true);
}

export function clearAuthenticatedUserContext(): void {
  if (!appInsights) {
    return;
  }

  appInsights.clearAuthenticatedUserContext();
}

// Helper function to track custom events
export function trackEvent(
  name: string,
  properties?: { [key: string]: any },
  measurements?: { [key: string]: number }
): void {
  if (appInsights) {
    appInsights.trackEvent({ name, properties, measurements });
  }
}

// Helper function to track exceptions
export function trackException(
  exception: Error,
  properties?: { [key: string]: any }
): void {
  if (appInsights) {
    appInsights.trackException({ exception, properties });
  }
}

// Helper function to track page views
export function trackPageView(
  name?: string,
  properties?: { [key: string]: any }
): void {
  if (appInsights) {
    appInsights.trackPageView({ name, properties });
  }
}
